import { useRef, useCallback, type FC, type CSSProperties } from 'react';
import clsx from 'clsx';

import { ResizeHandle } from '../../../components/ResizeHandle';
import { useResizeHandle } from '../../../hooks/useResizeHandle';
import { VideoTimeline } from './VideoTimeline';
import { useVideoTimeline } from '../hooks/useVideoTimeline';
import { useDebouncedInput } from '../hooks/useDebouncedInput';

/** Default video width for double-click reset */
const DEFAULT_WIDTH = 640;

export interface VideoViewerProps {
  src: string;
  title: string;
  width: number;
  height: number;
  isSelected: boolean;
  readonly?: boolean;
  onChangeSize: (width: number, height: number) => void;
  onChangeTitle: (title: string) => void;
}

/**
 * Video viewer component with playback, resize handles, and caption editing.
 * Includes timeline overlay for videos with timestamp parameters.
 */
export const VideoViewer: FC<VideoViewerProps> = ({
  src,
  title,
  width,
  height,
  isSelected,
  readonly = false,
  onChangeSize,
  onChangeTitle,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Timeline state and video events
  const { duration, currentTime, startTime, endTime, isLoading, showTimeline } = useVideoTimeline(videoRef, src);

  // Resize functionality (aspect ratio maintained, only width controls size)
  const {
    width: currentWidth,
    isDragging,
    handleResizeStart,
  } = useResizeHandle(containerRef, {
    initialWidth: width,
    initialHeight: height,
    defaultWidth: DEFAULT_WIDTH,
    disabled: readonly,
    onResize: onChangeSize,
  });

  // Caption with debounced save
  const caption = useDebouncedInput({
    initialValue: title,
    onSave: onChangeTitle,
  });

  // Prevent ProseMirror from capturing caption interactions
  const stopPropagation = useCallback((e: React.MouseEvent | React.DragEvent) => {
    e.stopPropagation();
    if (e.type === 'dragstart') e.preventDefault();
  }, []);

  const containerStyle: CSSProperties = {
    width: currentWidth || 'auto',
    maxWidth: '100%',
    pointerEvents: isDragging ? 'none' : 'all',
  };

  return (
    <div contentEditable={false} className={clsx('video-viewer my-4', showTimeline && 'has-custom-timeline')}>
      <div
        ref={containerRef}
        className={clsx(
          'relative mx-auto rounded-lg select-none overflow-hidden group',
          isDragging ? 'transition-none' : 'transition-[width] duration-150 ease-in-out',
          isSelected && 'ring-2 ring-blue-500',
        )}
        style={containerStyle}
      >
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- captions not available for user-uploaded videos */}
        <video
          ref={videoRef}
          src={src}
          title={title}
          controls={!isDragging && !isLoading}
          preload="metadata"
          playsInline
          onContextMenu={(e) => e.preventDefault()}
          controlsList="nodownload"
          className={clsx('w-full h-auto rounded-lg bg-dark-200', isLoading ? 'animate-pulse' : '')}
        >
          Your browser does not support the video tag.
        </video>

        {showTimeline && (
          <VideoTimeline
            duration={duration}
            currentTime={currentTime}
            startTime={startTime ?? null}
            endTime={endTime ?? null}
          />
        )}

        {!readonly && (
          <>
            <ResizeHandle side="left" onPointerDown={handleResizeStart('left')} />
            <ResizeHandle side="right" onPointerDown={handleResizeStart('right')} />
          </>
        )}
      </div>

      {readonly ? (
        title && (
          <p
            className="block mx-auto mt-1 text-xs text-center italic text-neutral-500 dark:text-neutral-400"
            style={{ maxWidth: currentWidth || '100%' }}
          >
            {title}
          </p>
        )
      ) : (
        <input
          type="text"
          draggable
          onDragStart={stopPropagation}
          onMouseDown={stopPropagation}
          placeholder="Add a caption..."
          value={caption.value}
          onChange={caption.onChange}
          onBlur={caption.onBlur}
          onKeyDown={caption.onKeyDown}
          className="block mx-auto mt-1 text-xs text-center italic bg-transparent border-none outline-none caret-dark-700 cursor-text select-text"
          style={{ width: '100%', maxWidth: currentWidth || '100%' }}
        />
      )}
    </div>
  );
};
